import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import { Menu } from "lucide-react";

export function Navbar() {
  const [overHero, setOverHero] = useState(true);
  const [isPageHero, setIsPageHero] = useState(false);

  useEffect(() => {
    const hero = document.getElementById("hero-section");
    const pageHero = document.getElementById("page-hero-section");
    const target = pageHero || hero;

    if (pageHero) setIsPageHero(true);

    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setOverHero(entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: "-1px 0px 0px 0px",
        threshold: 0,
      },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  let textColorClass = "text-[#151515]";
  if (overHero) {
    textColorClass = isPageHero
      ? "text-[#FFF9F4]"
      : "text-[#151515] md:text-[#FFF9F4]";
  }

  const linkStyle: React.CSSProperties = {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: 22,
    lineHeight: "29px",
    transition: "color 0.25s ease",
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
        <div
          className="w-full flex items-center justify-between max-w-[1440px] mx-auto pointer-events-auto"
          style={{ padding: "25px 30px 16px" }}
        >
          {/* Left: tagline */}
          <div className="flex-1">
            <a
              href="/"
              className={textColorClass}
              style={{ ...linkStyle, letterSpacing: "-0.05em" }}
            >
              - a bold design agency
            </a>
          </div>

          {/* Center: nav links — each independently styled */}
          <div className="hidden lg:flex items-center" style={{ gap: 40 }}>
            {[
              { label: "Expertise", href: "/services" },
              { label: "Works", href: "/portfolio" },
              { label: "About", href: "/about" },
              { label: "SEO Audit", href: "/seo-audit" },
            ].map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`hover:opacity-70 transition-opacity ${textColorClass}`}
                style={linkStyle}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right: CTA & Hamburger */}
          <div className="flex-1 flex justify-end items-center gap-6">
            <a
              href="/contact"
              className={`hidden lg:block hover:opacity-70 transition-opacity ${textColorClass}`}
              style={{
                ...linkStyle,
                textDecoration: "underline",
                textUnderlineOffset: 4,
              }}
            >
              Get Connected
            </a>

            <Sheet>
              <SheetTrigger asChild>
                <button
                  aria-label="Open menu"
                  className={`transition-opacity hover:opacity-70 flex items-center justify-center cursor-pointer pointer-events-auto ${textColorClass}`}
                >
                  <Menu size={28} />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="bg-[#141414] border-none text-[#FFF9F4] w-full sm:w-[400px]"
              >
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col gap-6 mt-12">
                  {[
                    { label: "Home", href: "/" },
                    { label: "About", href: "/about" },
                    { label: "Services", href: "/services" },
                    { label: "Portfolio", href: "/portfolio" },
                    { label: "FAQ", href: "/faq" },
                    { label: "Blog", href: "/blog" },
                    { label: "Free SEO Audit", href: "/seo-audit" },
                    { label: "Get Found Playbook", href: "/get-found" },
                    { label: "Contact", href: "/contact" },
                  ].map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      className="text-2xl hover:text-[#b85736] transition-colors"
                      style={{ fontFamily: "'Share Tech Mono', monospace" }}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </>
  );
}
